/**
 * Pure derivations for the profile form's address values.
 *
 * These live outside the page components on purpose: the defect they fix is a
 * ROUND TRIP defect, and a round trip can only be asserted end-to-end if both
 * ends are importable. `tests/unit/profileRoundTrip.test.js` drives the real
 * `api/transformers/userTransformer.js` into these two functions and back, which
 * is the only shape of test that can catch it — see the warning below.
 *
 * 🔑 ⛔ NEVER SEED THE EDITABLE STREET FROM `address.formatted`.
 * `formatted` is a DISPLAY string the API composes as
 *   `[street, city, state, zipCode, country].filter(Boolean).join(', ')`
 * (`transformAddress` -> `formatAddress` in `api/utils/formatting.js`). Seeding
 * the street input from it and then submitting that input back AS `street` makes
 * the stored street absorb the whole formatted address on every save:
 *
 *   before  street = "123 Test Street"
 *   save 1  street = "123 Test Street, Miami, FL, 33101, United States"
 *   save 2  street = "123 Test Street, Miami, FL, 33101, United States, Miami, ..."
 *
 * It grows without bound until `Joi.string().max(200)` starts rejecting the save
 * outright, and this address feeds shipping on every order. The editable value is
 * `street` and only `street`.
 *
 * ⚠️ A payload SHAPE assertion cannot catch this — the nested
 * `{ street, city, state, zipCode }` object is perfectly well formed either way.
 * Assert the VALUES against the stored row.
 */

// Pure: stored user -> the four flat address fields the profile forms edit.
export const deriveAddressFormValues = (user) => ({
  address: user?.profile?.address?.street ?? '',
  state: user?.profile?.address?.state ?? '',
  city: user?.profile?.address?.city ?? '',
  zip: user?.profile?.address?.zipCode ?? ''
});

// Pure: the four flat form fields -> the API's nested address object.
// Returns null when the address is incomplete, so the caller can omit the key
// entirely rather than submit a partial address the validator would reject.
export const buildAddressPayload = ({ address, city, state, zip } = {}) => {
  const street = (address ?? '').trim();
  const trimmedCity = (city ?? '').trim();
  const trimmedState = (state ?? '').trim();
  const zipCode = (zip ?? '').trim();

  const isComplete = Boolean(street && trimmedCity && trimmedState && zipCode);

  return isComplete
    ? { street, city: trimmedCity, state: trimmedState, zipCode }
    : null;
};
